// GRW-36: TanStack Query bindings for events (industry days, conferences,
// meetings). The four RPCs live on zou's ProjectDataService; tenant and caller
// come from the gateway principal.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { projectDataClient } from '../../lib/connect/clients';
import { useTenantId } from '../Delivery/_shared/hooks/useTenantId';
export const EVENTS_KEY = 'events';
export const EVENT_KINDS = [
    {
        value: 'INDUSTRY_DAY',
        label: 'Industry day'
    },
    {
        value: 'CONFERENCE',
        label: 'Conference'
    },
    {
        value: 'MEETING',
        label: 'Meeting'
    },
    {
        value: 'CALL',
        label: 'Call'
    },
    {
        value: 'SITE_VISIT',
        label: 'Site visit'
    },
    {
        value: 'OTHER',
        label: 'Other'
    }
];
export function eventKindLabel(kind) {
    return EVENT_KINDS.find((k)=>k.value === kind)?.label ?? '';
}
/** Search the tenant's events by title (server ILIKE, newest first). */ export function useEvents(params = {}) {
    const tenantId = useTenantId();
    const search = (params.search ?? '').trim();
    const { relevantToKind, relevantToId, limit = 20 } = params;
    return useQuery({
        queryKey: [
            EVENTS_KEY,
            'list',
            tenantId,
            search,
            relevantToKind,
            relevantToId,
            limit
        ],
        enabled: !!tenantId && (params.enabled ?? true),
        queryFn: async ()=>(await projectDataClient.listEvents({
                search,
                relevantToKind,
                relevantToId,
                limit
            })).events
    });
}
export function useEvent(eventId) {
    const tenantId = useTenantId();
    return useQuery({
        queryKey: [
            EVENTS_KEY,
            'detail',
            tenantId,
            eventId
        ],
        enabled: !!tenantId && eventId !== '',
        queryFn: ()=>projectDataClient.getEvent({
                eventId
            })
    });
}
/** Create (empty id) or edit an event. Lists refetch; a cached detail is dropped. */ export function useUpsertEvent() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (event)=>{
            const res = await projectDataClient.upsertEvent({
                event
            });
            if (!res.event) throw new Error('The event could not be saved.');
            return res.event;
        },
        onSuccess: ()=>queryClient.invalidateQueries({
                queryKey: [
                    EVENTS_KEY
                ]
            })
    });
}
/** Add or remove one attendee / host / relevance link on an event. */ export function useSetEventLink() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (req)=>projectDataClient.setEventLink(req),
        onSuccess: ()=>queryClient.invalidateQueries({
                queryKey: [
                    EVENTS_KEY
                ]
            })
    });
}
/** Calendar date supplied by the event API; avoid shifting midnight across time zones. */ export function eventDescription(event) {
    const day = /^(\d{4})-(\d{2})-(\d{2})/.exec(event.startsAt);
    const months = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec'
    ];
    const date = day ? `${Number(day[3])} ${months[Number(day[2]) - 1]} ${day[1]}` : '';
    return [
        eventKindLabel(event.kind),
        date
    ].filter(Boolean).join(' · ');
}
